<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Tổng hợp thưởng AdHoc theo chương trình'">
          <template v-slot:preview>
            <div class="dash-filter mb-5">
              <b-row class="align-items-end">
                <b-col md="5">
                  <label class="dash-filter__label">Chương trình</label>
                  <Autosuggest
                    :model.sync="filter.programText"
                    :suggestions="filteredPrograms"
                    placeholder="chương trình AdHoc"
                    :limit="15"
                    @select="onSelectedProgram"
                    @change="onInputProgram"
                    suggestionName="suggestionName"
                  />
                </b-col>
                <b-col md="2">
                  <label class="dash-filter__label">Từ ngày</label>
                  <!-- Kỳ cố định theo thời gian hiệu lực của chương trình -->
                  <b-form-input
                    size="sm"
                    :value="filter.fromDate"
                    placeholder="Từ ngày"
                    disabled
                  ></b-form-input>
                </b-col>
                <b-col md="2">
                  <label class="dash-filter__label">Đến ngày</label>
                  <!-- Kỳ cố định theo thời gian hiệu lực của chương trình -->
                  <b-form-input
                    size="sm"
                    :value="filter.toDate"
                    placeholder="Đến ngày"
                    disabled
                  ></b-form-input>
                </b-col>
                <b-col md="3">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-6"
                    :disabled="onLoading"
                    @click="onFilter"
                    >Xem</b-button
                  >
                  <b-button
                    v-if="report"
                    variant="outline-primary"
                    size="sm"
                    class="ml-2"
                    :disabled="onLoading"
                    @click="downloadExcel"
                  >
                    <i class="far fa-file-excel mr-1"></i>Xuất Excel
                  </b-button>
                </b-col>
              </b-row>
            </div>

            <vcl-table
              v-if="onLoading"
              :speed="5"
              :animate="true"
              :columns="6"
            ></vcl-table>

            <div v-else-if="!report" class="dash-placeholder">
              Chọn chương trình rồi bấm Xem để tính thưởng realtime
            </div>

            <template v-else>
              <div class="dash-summary mb-5">
                <div class="dash-summary__card dash-summary__card--main">
                  <div class="dash-summary__label">Thưởng realtime</div>
                  <div class="dash-summary__value">
                    {{ displayAmount(report.totals.amount) }}
                  </div>
                  <div class="dash-summary__meta">
                    {{ report.program.name }} · {{ displayPeriod(report.period) }}
                  </div>
                </div>
                <div class="dash-summary__card">
                  <div class="dash-summary__label">Đã chốt sổ</div>
                  <div class="dash-summary__value">
                    {{ displayAmount(report.program.spentAmount) }}
                  </div>
                </div>
                <div class="dash-summary__card">
                  <div class="dash-summary__label">Ngân sách</div>
                  <div class="dash-summary__value">
                    {{
                      report.program.budget
                        ? displayAmount(report.program.budget)
                        : 'Không giới hạn'
                    }}
                  </div>
                  <div
                    v-if="report.program.budgetUsedPercent !== null"
                    class="dash-summary__meta"
                  >
                    Đã chốt {{ report.program.budgetUsedPercent }}% · realtime
                    {{ realtimeBudgetPercent }}%
                    <b-badge
                      v-if="budgetStatusName(report.program.budgetStatus)"
                      :variant="budgetVariant(report.program.budgetStatus)"
                      class="ml-1"
                      >{{ budgetStatusName(report.program.budgetStatus) }}</b-badge
                    >
                  </div>
                </div>
                <div class="dash-summary__card">
                  <div class="dash-summary__label">Nơi nhận</div>
                  <div class="dash-summary__value">
                    {{ displayQuantity(report.totals.receiverCount) }}
                  </div>
                </div>
              </div>

              <div v-if="!report.isActive" class="dash-placeholder mb-4">
                Chương trình không ở trạng thái áp dụng trong kỳ này nên chưa phát
                sinh thưởng
              </div>

              <b-table
                :items="receiverRows"
                :fields="fields"
                bordered
                hover
                show-empty
                empty-text="Chưa có thưởng AdHoc nào phát sinh trong kỳ"
              >
                <template v-slot:cell(receiverName)="row">
                  <b-button
                    variant="link"
                    class="p-0 text-left dash-link"
                    @click="openReceiver(row.item)"
                    >{{ row.item.receiverName }}</b-button
                  >
                  <div class="text-muted small">{{ row.item.receiverCode }}</div>
                </template>
                <template v-slot:cell(quantity)="row">
                  {{ displayQuantity(row.item.quantity) }}
                </template>
                <template v-slot:cell(billCount)="row">
                  {{ displayQuantity(row.item.billCount) }}
                </template>
                <template v-slot:cell(employeeCount)="row">
                  {{ displayQuantity(row.item.employeeCount) }}
                </template>
                <template v-slot:cell(amount)="row">
                  <span class="font-weight-bold">{{
                    displayAmount(row.item.amount)
                  }}</span>
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0"
                    title="Xem từng nhân viên"
                    @click="openReceiver(row.item)"
                  >
                    <i class="fas fa-eye text-primary"></i>
                  </b-button>
                </template>
              </b-table>
            </template>
          </template>
        </KTCodePreview>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Thưởng AdHoc THEO CHƯƠNG TRÌNH, tính realtime bằng engine (gồm cả phần chưa chốt sổ):
 * chọn một chương trình -> bảng theo cửa hàng / bộ phận CR -> bấm để xem từng nhân viên
 * ở báo cáo AdHoc của cửa hàng (đã lọc sẵn chương trình).
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import moment from 'moment';
import xlsx from 'xlsx';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile, removeAccents } from '@/utils/common';
import {
  COMMISSION_ADHOC_STATUS_NAME,
  COMMISSION_ADHOC_BUDGET_STATUS_NAME,
  COMMISSION_ADHOC_BUDGET_STATUS_VARIANT,
} from './commission.constants';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

// Đủ cho ô chọn chương trình; API giới hạn tối đa 200
const PROGRAM_LIMIT = 200;

export default {
  components: { KTCodePreview, Autosuggest, VclTable },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      report: null,
      programOptions: [],
      filteredPrograms: [],
      filter: {
        adhocId: null,
        programText: '',
        fromDate: '',
        toDate: '',
      },
      fields: [
        {
          key: 'receiverName',
          label: 'Cửa hàng / bộ phận',
          thStyle: thStyle('38%'),
        },
        {
          key: 'quantity',
          label: 'SL bán',
          thStyle: thStyle('11%'),
          tdClass: 'text-center',
        },
        {
          key: 'billCount',
          label: 'Hoá đơn',
          thStyle: thStyle('11%'),
          tdClass: 'text-center',
        },
        {
          key: 'employeeCount',
          label: 'Nhân sự nhận',
          thStyle: thStyle('12%'),
          tdClass: 'text-center',
        },
        {
          key: 'amount',
          label: 'Thưởng AdHoc',
          thStyle: thStyle('22%'),
          tdClass: 'text-right',
        },
        { key: 'actions', label: '', thStyle: thStyle('6%'), tdClass: 'text-center' },
      ],
    };
  },
  computed: {
    /** Cửa hàng trước, bộ phận CR sau — cùng một bảng */
    receiverRows() {
      if (!this.report) {
        return [];
      }
      const stores = (this.report.stores || []).map((row) => ({
        ...row,
        key: `store-${row.storeId}`,
        receiverName: row.storeName,
        receiverCode: row.storeShortName,
      }));
      const departments = (this.report.departments || []).map((row) => ({
        ...row,
        key: `cr-${row.departmentId}`,
        receiverName: row.departmentName,
        receiverCode: 'CR',
      }));
      return stores.concat(departments);
    },
    realtimeBudgetPercent() {
      const budget = Number(this.report && this.report.program.budget);
      if (!budget) {
        return null;
      }
      return Math.round((Number(this.report.totals.amount) / budget) * 1000) / 10;
    },
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng V2' },
      { title: 'Tổng hợp thưởng AdHoc theo chương trình' },
    ]);
    this.restoreFilterFromQuery();
    this.fetchPrograms();
  },
  methods: {
    budgetVariant(status) {
      return COMMISSION_ADHOC_BUDGET_STATUS_VARIANT[Number(status)] || 'secondary';
    },
    budgetStatusName(status) {
      return COMMISSION_ADHOC_BUDGET_STATUS_NAME[Number(status)] || '';
    },

    /* ------------------------------ Bộ lọc -------------------------------- */
    restoreFilterFromQuery() {
      const query = this.$route.query || {};
      if (query.adhocId) {
        this.filter.adhocId = Number(query.adhocId);
      }
    },
    syncFilterToQuery() {
      const query = { adhocId: this.filter.adhocId || undefined };
      this.$router.replace({ query }).catch(() => {});
    },
    fetchPrograms() {
      ApiService.setHeader();
      ApiService.query('commission-adhocs', {
        params: { page: 1, limit: PROGRAM_LIMIT },
      })
        .then(({ data }) => {
          this.programOptions = ((data.data && data.data.items) || []).map(
            (program) => ({
              ...program,
              suggestionName: `${program.name} · ${
                COMMISSION_ADHOC_STATUS_NAME[program.status] || ''
              }`,
            }),
          );
          this.filteredPrograms = this.filterProgramOptions('');
          // Chương trình từ URL: có danh sách rồi mới gán được tên và tải báo cáo
          const selected = this.programOptions.find(
            (program) => program.id === this.filter.adhocId,
          );
          if (selected) {
            this.filter.programText = selected.suggestionName;
            this.fetchReport();
          }
        })
        .catch((error) =>
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được danh sách chương trình'),
          ),
        );
    },
    filterProgramOptions(text) {
      const keyword = removeAccents(text || '').toLowerCase();
      return this.programOptions
        .filter(
          (program) =>
            removeAccents(program.suggestionName).toLowerCase().indexOf(keyword) >
            -1,
        )
        .slice(0, 15);
    },
    onInputProgram(text) {
      const selected = this.programOptions.find(
        (program) => program.id === this.filter.adhocId,
      );
      if (!selected || selected.suggestionName !== text) {
        this.filter.adhocId = null;
      }
      this.filteredPrograms = this.filterProgramOptions(text);
    },
    /** Chọn chương trình: kỳ = thời gian hiệu lực, cắt tới hôm nay (chỉ để hiển thị) */
    onSelectedProgram(option) {
      const program = option.item;
      this.filter.adhocId = program.id;
      this.filter.programText = program.suggestionName;
      const today = moment();
      const end = program.endDate ? moment(program.endDate) : today;
      this.filter.fromDate = moment(program.startDate).format(DATE_INPUT_FORMAT);
      this.filter.toDate = (end.isAfter(today) ? today : end).format(
        DATE_INPUT_FORMAT,
      );
    },

    /* ----------------------------- Tải dữ liệu ----------------------------- */
    onFilter() {
      if (!this.filter.adhocId) {
        return makeToastFaile('Vui lòng chọn chương trình!');
      }
      this.syncFilterToQuery();
      this.fetchReport();
    },
    fetchReport() {
      this.onLoading = true;
      ApiService.setHeader();
      // Không gửi ngày: API tự lấy thời gian hiệu lực của chương trình, cắt tới hôm nay
      ApiService.query(`commission-adhocs/${this.filter.adhocId}/realtime`)
        .then(({ data }) => {
          this.report = data.data;
          // Kỳ thực tế API đã tính
          this.filter.fromDate = this.toInputDate(this.report.period.fromDate);
          this.filter.toDate = this.toInputDate(this.report.period.toDate);
        })
        .catch((error) => {
          this.report = null;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tính được thưởng của chương trình'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    /** Từng nhân viên của cửa hàng / bộ phận — báo cáo AdHoc đã lọc sẵn chương trình */
    openReceiver(row) {
      const query = {
        fromDate: this.toInputDate(this.report.period.fromDate),
        toDate: this.toInputDate(this.report.period.toDate),
        adhocId: this.report.program.id,
        adhocName: this.report.program.name,
      };
      if (row.departmentId) {
        query.departmentId = row.departmentId;
        query.departmentName = row.departmentName;
      } else {
        query.storeId = row.storeId;
      }
      this.$router.push({ name: 'commission-reports-adhoc', query });
    },
    downloadExcel() {
      const report = this.report;
      const sheet = xlsx.utils.aoa_to_sheet([
        ['Chương trình', report.program.name],
        ['Kỳ', this.displayPeriod(report.period)],
        ['Thưởng realtime', report.totals.amount],
        ['Đã chốt sổ', report.program.spentAmount],
        [
          'Ngân sách',
          report.program.budget ? Number(report.program.budget) : 'Không giới hạn',
        ],
        [],
        ['STT', 'Mã', 'Cửa hàng / bộ phận', 'SL bán', 'Hoá đơn', 'Nhân sự nhận', 'Thưởng AdHoc'],
        ...this.receiverRows.map((row, index) => [
          index + 1,
          row.receiverCode,
          row.receiverName,
          row.quantity,
          row.billCount,
          row.employeeCount,
          row.amount,
        ]),
        [],
        ['', '', 'Tổng cộng', '', '', '', report.totals.amount],
      ]);
      sheet['!cols'] = [
        { wch: 6 },
        { wch: 14 },
        { wch: 40 },
        { wch: 10 },
        { wch: 10 },
        { wch: 14 },
        { wch: 16 },
      ];
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, sheet, 'Theo cua hang');
      const safeName = String(report.program.name || 'AdHoc')
        .replace(/[\\/:*?"<>|]/g, '')
        .slice(0, 60);
      xlsx.writeFile(workbook, `AdHoc_${report.program.id}_${safeName}.xlsx`);
    },
  },
};
</script>

<style scoped>
.dash-filter {
  background: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  padding: 16px 20px;
}
.dash-filter__label {
  font-size: 12px;
  font-weight: 600;
  color: #7e8299;
  margin-bottom: 4px;
}
.dash-placeholder {
  text-align: center;
  color: #7e8299;
  padding: 32px 16px;
  border: 1px dashed #e4e6ef;
  border-radius: 6px;
}
.dash-summary {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  overflow: hidden;
}
.dash-summary__card {
  padding: 14px 18px;
  border-right: 1px solid #ecf0f3;
}
.dash-summary__card:last-child {
  border-right: 0;
}
.dash-summary__card--main .dash-summary__value {
  color: #3699ff;
}
.dash-summary__label {
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.dash-summary__value {
  font-size: 20px;
  font-weight: 700;
  color: #181c32;
}
.dash-summary__meta {
  font-size: 12px;
  color: #7e8299;
  margin-top: 4px;
}
.dash-link,
.dash-link:focus {
  color: #3699ff !important;
  font-weight: 600;
}
.dash-link:hover {
  color: #187de4 !important;
  text-decoration: underline;
}
@media (max-width: 767.98px) {
  .dash-summary {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
